import { escapeHtml } from "@/utils";

export const SEEKTOKEN_GATEWAY_BASE_URL = "https://www.126token.com/v1";
export const USER_GUIDE_DOWNLOAD_PATH = "/downloads/user-api-usage-v0.5.1.md";
export const ADMIN_GUIDE_DOWNLOAD_PATH = "/downloads/admin-console-operations-v1.0.0.md";
export const POSIX_SETUP_SCRIPT_PATH = "/downloads/configure-codex-seektoken.sh";
export const POWERSHELL_SETUP_SCRIPT_PATH = "/downloads/configure-codex-seektoken.ps1";

interface GuideDownload {
  href: string;
  label: string;
  hint: string;
}

interface GuideCard {
  id: string;
  title: string;
  detail: string;
}

interface GuideSection {
  id: string;
  title: string;
  summary: string;
  body: string;
}

interface GuideDocument {
  eyebrow: string;
  title: string;
  subtitle: string;
  version: string;
  updatedAt: string;
  downloads: GuideDownload[];
  cards: GuideCard[];
  sections: GuideSection[];
}

function renderGuideDownloads(downloads: GuideDownload[]): string {
  return downloads.map((item) => `
    <a class="docs-download-card" href="${escapeHtml(item.href)}" download>
      <strong>${escapeHtml(item.label)}</strong>
      <span>${escapeHtml(item.hint)}</span>
    </a>
  `).join("");
}

function renderGuideCards(cards: GuideCard[]): string {
  return cards.map((card) => `
    <a class="docs-card" href="#${escapeHtml(card.id)}">
      <strong>${escapeHtml(card.title)}</strong>
      <span>${escapeHtml(card.detail)}</span>
    </a>
  `).join("");
}

function renderGuideSections(sections: GuideSection[]): string {
  return sections.map((section) => `
    <section id="${escapeHtml(section.id)}" class="docs-section">
      <div class="docs-section-header">
        <div>
          <h3>${escapeHtml(section.title)}</h3>
          <p class="docs-section-summary">${escapeHtml(section.summary)}</p>
        </div>
        <a class="ghost-button docs-anchor-link" href="#${escapeHtml(section.id)}">定位</a>
      </div>
      <div class="docs-section-body">
        ${section.body}
      </div>
    </section>
  `).join("");
}

function renderGuide(document: GuideDocument): string {
  return `
    <section class="panel panel-fill docs-panel">
      <div class="docs-hero">
        <div class="docs-hero-copy">
          <p class="eyebrow">${escapeHtml(document.eyebrow)}</p>
          <h2>${escapeHtml(document.title)}</h2>
          <p class="docs-summary">${escapeHtml(document.subtitle)}</p>
          <p class="doc-version">版本 ${escapeHtml(document.version)} · 更新日期 ${escapeHtml(document.updatedAt)}</p>
        </div>
        <div class="docs-download-grid">
          ${renderGuideDownloads(document.downloads)}
        </div>
      </div>
      <div class="docs-card-grid">
        ${renderGuideCards(document.cards)}
      </div>
      <div class="docs-content">
        ${renderGuideSections(document.sections)}
      </div>
    </section>
  `;
}

const adminGuide: GuideDocument = {
  eyebrow: "Admin Guide",
  title: "管理员使用文档",
  subtitle: "按后台子系统拆分日常操作、巡检入口和常见异常处理，减少口口相传式支持。",
  version: "v1.0.0",
  updatedAt: "2026-07-09",
  downloads: [
    {
      href: ADMIN_GUIDE_DOWNLOAD_PATH,
      label: "下载管理员 Markdown",
      hint: "后台操作手册"
    },
    {
      href: USER_GUIDE_DOWNLOAD_PATH,
      label: "下载用户 Markdown",
      hint: "发给普通用户的接入说明"
    }
  ],
  cards: [
    {
      id: "admin-systems",
      title: "系统总览",
      detail: "先看每个后台模块分别负责什么"
    },
    {
      id: "admin-users",
      title: "用户与权限",
      detail: "开户、停用、重置密码、API Key 支持"
    },
    {
      id: "admin-wallets",
      title: "钱包与订单",
      detail: "充值、产品、订单、退款与导出"
    },
    {
      id: "admin-gpt",
      title: "GPT 账号与模型",
      detail: "账号凭据、服务实例、账号节点、模型管理"
    },
    {
      id: "admin-health",
      title: "系统状态与排障",
      detail: "健康检查、请求日志与首要排查点"
    },
    {
      id: "admin-onboarding",
      title: "用户接入 SOP",
      detail: "面对普通用户时该给什么资料"
    }
  ],
  sections: [
    {
      id: "admin-systems",
      title: "系统总览",
      summary: "先按后台子系统理解职责边界，再进入具体操作。",
      body: `
        <table class="docs-table">
          <thead>
            <tr>
              <th>后台模块</th>
              <th>入口</th>
              <th>管理员最常做的事</th>
              <th>出现问题先看哪里</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>系统看板</td>
              <td><code>系统看板</code></td>
              <td>看今日请求量、活跃用户、错误率、最近调用日志</td>
              <td>请求日志、错误率、最近失败记录</td>
            </tr>
            <tr>
              <td>用户与权限系统</td>
              <td><code>用户管理</code></td>
              <td>新建用户、停用用户、重置密码、协助用户管理 API Key</td>
              <td>用户状态、API Key 状态、角色是否包含 <code>admin</code></td>
            </tr>
            <tr>
              <td>钱包与订单系统</td>
              <td><code>钱包履历</code> / <code>产品管理</code> / <code>订单管理</code></td>
              <td>配置充值方案、人工充值、订单补录、退款、导出流水</td>
              <td>订单状态、钱包流水类型、产品是否启用</td>
            </tr>
            <tr>
              <td>GPT 账号与模型系统</td>
              <td><code>GPT 账号</code></td>
              <td>维护 ChatGPT 账号凭据、服务实例及账号节点，查看节点故障审计，维护公共模型目录</td>
              <td><code>auth.json</code> 状态、节点健康与冷却、服务实例归属、模型来源</td>
            </tr>
            <tr>
              <td>网关健康系统</td>
              <td><code>系统状态</code></td>
              <td>确认上游可用性、判断是本服务问题还是上游问题</td>
              <td>各层健康检查、返回状态、错误信息、过期时间</td>
            </tr>
          </tbody>
        </table>
        <div class="docs-callout">
          <strong>建议运维口径</strong>
          <p>管理员面对普通用户时，不要把后台细节直接甩给用户。用户只需要拿到自己的接入文档、网关地址、推荐模型和常见报错处理步骤。</p>
        </div>
      `
    },
    {
      id: "admin-users",
      title: "用户与权限系统",
      summary: "用户生命周期相关动作统一在这里处理。",
      body: `
        <h4>典型场景</h4>
        <ul>
          <li>新用户开通账号并发放初始点数。</li>
          <li>用户忘记密码，需要重置登录密码。</li>
          <li>用户说 API Key 不可用，需要检查 Key 状态、是否被停用、是否已删除。</li>
          <li>需要给内部同事管理员权限，确认角色中包含 <code>admin</code>。</li>
        </ul>
        <h4>推荐操作顺序</h4>
        <ol>
          <li>先搜索用户邮箱或用户名，避免重复开户。</li>
          <li>新增用户后，如果需要立即可用，再视情况人工充值或给出自助充值路径。</li>
          <li>用户报障时优先核对用户状态、Key 状态、当天请求和错误数。</li>
          <li>重置密码后让用户重新登录，不要代替用户保管 API Key。</li>
        </ol>
        <table class="docs-table">
          <thead>
            <tr>
              <th>用户反馈</th>
              <th>管理员先检查</th>
              <th>处理方式</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>无法登录</td>
              <td>用户是否停用、是否忘记密码</td>
              <td>必要时执行重置密码；被停用则明确告知原因</td>
            </tr>
            <tr>
              <td>401 / Invalid API key</td>
              <td>Key 是否启用、是否复制完整明文</td>
              <td>引导用户重新生成 Key，不要尝试“找回”旧明文</td>
            </tr>
            <tr>
              <td>账号没有权限进后台</td>
              <td>角色字段中是否包含 <code>admin</code></td>
              <td>只给明确需要运维职责的人开管理权限</td>
            </tr>
          </tbody>
        </table>
      `
    },
    {
      id: "admin-wallets",
      title: "钱包与订单系统",
      summary: "充值、产品、订单、退款要按一条资金链路看，不要分散处理。",
      body: `
        <h4>三个入口分别做什么</h4>
        <ul>
          <li><code>产品管理</code>：维护充值方案、面额、点数换算、是否启用。</li>
          <li><code>订单管理</code>：查看订单状态、补录支付、执行退款。</li>
          <li><code>钱包履历</code>：看某个用户的到账、消费、退款明细，并导出 CSV。</li>
        </ul>
        <h4>建议 SOP</h4>
        <ol>
          <li>先保证产品方案正确且启用，再让用户下单。</li>
          <li>用户已付款但未到账时，先查订单状态，再查对应钱包流水。</li>
          <li>退款时同时核对订单状态和钱包余额变化，避免只退订单不退点数。</li>
          <li>对账或财务复盘时优先导出钱包流水，而不是手抄后台表格。</li>
        </ol>
        <div class="docs-callout">
          <strong>高频误区</strong>
          <p>看到用户余额不对时，不要直接改数据库。先确认是充值未到账、消费计费、退款回滚，还是产品配置本身有误。</p>
        </div>
      `
    },
    {
      id: "admin-gpt",
      title: "GPT 账号与模型系统",
      summary: "这部分同时决定模型可用性和上游稳定性，是管理员最需要留文档的区域。",
      body: `
        <h4>服务实例与账号节点</h4>
        <ul>
          <li>在 <code>GPT 账号</code> 页面维护账号凭据并检查各自 <code>auth.json</code> 是否存在、是否过期；系统不再设置全局活跃账号。</li>
          <li>通道只绑定服务实例；服务实例内部可以有多个账号执行节点，每个节点固定一个账号和独立进程。</li>
          <li>需要使用另一个账号时，在同一服务实例中新建节点，不要修改已有节点的账号。</li>
          <li>账号额度或认证失败会冷却当前节点；新会话可以选择其他健康节点，已有会话不会静默换账号或立即重试。</li>
        </ul>
        <h4>模型管理</h4>
        <ul>
          <li>模型名是对外给用户使用的名字，例如 <code>gpt-5.4</code>。</li>
          <li>上游模型名是发给被 Channel 选中的服务实例节点的真实模型标识。</li>
          <li>新增和删除模型会同步维护公开目录与精确点数费率；执行节点不维护另一份模型目录。</li>
        </ul>
        <table class="docs-table">
          <thead>
            <tr>
              <th>现象</th>
              <th>优先判断</th>
              <th>建议动作</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>大量 502 / 503</td>
              <td>被 Channel 选中的上游 bridge 或外部接口是否不可达</td>
              <td>先看 <code>系统状态</code>，再看服务实例内账号节点的健康、冷却和模型配置</td>
            </tr>
            <tr>
              <td>某个模型突然不可用</td>
              <td>模型是否被删、上游模型名是否改动</td>
              <td>核对模型配置，不要直接让用户换 Key</td>
            </tr>
            <tr>
              <td>账号节点频繁冷却</td>
              <td>该节点账号 token 是否将过期、额度是否耗尽、是否存在区域网络问题</td>
              <td>查看节点故障审计；续期原账号，或在同一服务实例中新建账号节点</td>
            </tr>
          </tbody>
        </table>
      `
    },
    {
      id: "admin-health",
      title: "系统状态与排障",
      summary: "管理员支持要先判断问题落在哪一层，而不是一上来让用户重试。",
      body: `
        <h4>排查顺序</h4>
        <ol>
          <li>先看 <code>系统状态</code> 页面：是网关、bridge、上游哪个环节异常。</li>
          <li>再看 <code>系统看板</code> 中的最近请求日志：错误是否集中于某个用户、模型、时间段。</li>
          <li>最后回到具体系统处理：用户问题去 <code>用户管理</code>，模型问题去 <code>GPT 账号</code>。</li>
        </ol>
        <table class="docs-table">
          <thead>
            <tr>
              <th>错误类型</th>
              <th>通常表示</th>
              <th>管理员动作</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>401</td>
              <td>Key 无效、用户被停用，或 <code>auth.json</code> 内容错误</td>
              <td>先核对 Key 和用户状态，再把用户文档发给对方自查</td>
            </tr>
            <tr>
              <td>429</td>
              <td>配额或频率限制触发</td>
              <td>核对用户当天用量、分钟速率、是否需要升级产品或临时扩容</td>
            </tr>
            <tr>
              <td>502 / 503</td>
              <td>上游 bridge 或外部模型链路异常</td>
              <td>先查健康页，再查 GPT 账号与模型系统</td>
            </tr>
          </tbody>
        </table>
        <div class="docs-callout">
          <strong>支持原则</strong>
          <p>管理员回复用户时，优先给“可执行下一步”，例如“重新生成 API Key”或“完全退出 Codex App 后重开”，不要只回复“稍后再试”。</p>
        </div>
      `
    },
    {
      id: "admin-onboarding",
      title: "用户接入 SOP",
      summary: "普通用户最需要的是面向自己终端环境的文档，不是后台截图。",
      body: `
        <h4>给用户的标准资料</h4>
        <ul>
          <li>用户端使用文档：直接发本页顶部的“下载用户 Markdown”。</li>
          <li>网关地址：统一使用 <code>${escapeHtml(SEEKTOKEN_GATEWAY_BASE_URL)}</code>，不要再发送旧 IP 或占位地址。</li>
          <li>推荐模型：默认建议一个主模型和一个轻量模型。</li>
          <li>充值说明：让用户去 <code>订单</code> 标签页充值，到 <code>钱包履历</code> 标签页查看余额和流水。</li>
        </ul>
        <h4>面向不同用户终端的回答模板</h4>
        <ul>
          <li>macOS 桌面 App 与 CLI：使用同一份 <code>~/.codex/config.toml</code> 和 <code>~/.codex/auth.json</code>。</li>
          <li>Linux 与 WSL：使用 Linux 用户目录下的两份配置文件，不要写到 Windows 主机侧。</li>
          <li>Windows PowerShell：使用 <code>$HOME&#92;.codex&#92;config.toml</code> 和 <code>$HOME&#92;.codex&#92;auth.json</code>。</li>
          <li>服务器 / CI：使用文件和挂载式 secret，不通过 API Key 环境变量传递。</li>
        </ul>
        <div class="docs-callout">
          <strong>落地建议</strong>
          <p>以后新增接入方式时，先更新这两份 Markdown，再通知管理员对外发送新版本，避免每个管理员各写一套口径。</p>
        </div>
      `
    }
  ]
};

const userGuide: GuideDocument = {
  eyebrow: "User Guide",
  title: "普通用户使用文档",
  subtitle: "从开户、拿 Key、按终端系统配置，到充值与排障，尽量让用户能自助完成接入。",
  version: "v0.5.1",
  updatedAt: "2026-08-06",
  downloads: [
    {
      href: USER_GUIDE_DOWNLOAD_PATH,
      label: "下载 Markdown",
      hint: "用户接入与使用说明"
    },
    {
      href: POSIX_SETUP_SCRIPT_PATH,
      label: "下载 macOS / Linux 脚本",
      hint: "同时适用于 WSL 与服务器"
    },
    {
      href: POWERSHELL_SETUP_SCRIPT_PATH,
      label: "下载 Windows 脚本",
      hint: "PowerShell 直接执行"
    }
  ],
  cards: [
    {
      id: "user-start",
      title: "开通前准备",
      detail: "先注册登录，再拿到 API Key"
    },
    {
      id: "user-config-files",
      title: "两份配置文件",
      detail: "config.toml 与 auth.json 分开配置"
    },
    {
      id: "user-cli",
      title: "macOS / Linux CLI",
      detail: "macOS、Linux 和 WSL 共用脚本"
    },
    {
      id: "user-windows",
      title: "Windows / WSL",
      detail: "PowerShell 脚本与 WSL 的差异"
    },
    {
      id: "user-server",
      title: "服务器 / CI",
      detail: "文件与挂载式 secret 方案"
    },
    {
      id: "user-console",
      title: "用户控制台",
      detail: "查看 Key、订单、余额和使用记录"
    },
    {
      id: "user-faq",
      title: "常见问题",
      detail: "401、429、502 等报错处理"
    }
  ],
  sections: [
    {
      id: "user-start",
      title: "开通前准备",
      summary: "先完成账号和 API Key，再按固定网关地址配置客户端。",
      body: `
        <ol>
          <li>在注册页完成注册并登录用户控制台。</li>
          <li>进入 <code>API Key</code> 标签页，点击“创建 API Key”，保存只显示一次的明文 Key。</li>
          <li>官方网关地址固定为 <code>${escapeHtml(SEEKTOKEN_GATEWAY_BASE_URL)}</code>；推荐模型可向管理员确认，例如 <code>gpt-5.4</code> 与 <code>gpt-5.4-mini</code>。</li>
        </ol>
        <div class="docs-callout">
          <strong>注意</strong>
          <p>API Key 明文只展示一次。如果丢失，请直接删除旧 Key 并重新生成，不要等待管理员“找回”。</p>
        </div>
      `
    },
    {
      id: "user-config-files",
      title: "分别配置 config.toml 与 auth.json",
      summary: "网关和模型写入 config.toml，API Key 单独写入 auth.json，不使用环境变量。",
      body: `
        <h4>文件位置</h4>
        <table class="docs-table">
          <thead><tr><th>系统</th><th>目录</th></tr></thead>
          <tbody>
            <tr><td>macOS / Linux / WSL</td><td><code>~/.codex/</code></td></tr>
            <tr><td>Windows PowerShell</td><td><code>$HOME&#92;.codex&#92;</code></td></tr>
          </tbody>
        </table>
        <h4>1. config.toml：网关与模型</h4>
        <pre><code>model = "gpt-5.4"
model_provider = "seektoken"

[model_providers.seektoken]
name = "SeekToken"
base_url = "${escapeHtml(SEEKTOKEN_GATEWAY_BASE_URL)}"
wire_api = "responses"
requires_openai_auth = true</code></pre>
        <h4>2. auth.json：API Key</h4>
        <pre><code>{
  "auth_mode": "apikey",
  "OPENAI_API_KEY": "SK_xxxxxxxxxxxxxxxxxxxxx"
}</code></pre>
        <div class="docs-callout">
          <strong>为什么要分开</strong>
          <p><code>config.toml</code> 只保存非敏感的 provider 配置；<code>auth.json</code> 保存 API Key，并应限制为当前用户可读。两个文件都是持久化配置，Codex App 和 CLI 使用同一套文件，不需要 <code>launchctl</code>、<code>setx</code> 或 shell 环境变量。</p>
        </div>
      `
    },
    {
      id: "user-cli",
      title: "macOS / Linux / WSL 一键配置",
      summary: "脚本已内置官方网关地址，只需输入 Key；更新配置前会自动备份旧文件。",
      body: `
        <pre><code>chmod 700 configure-codex-seektoken.sh
./configure-codex-seektoken.sh</code></pre>
        <p>也可以直接传入模型参数，API Key 仍在终端中隐藏输入：</p>
        <pre><code>./configure-codex-seektoken.sh \
  --model gpt-5.4</code></pre>
        <p>macOS 桌面 App 配置后请 <strong>Cmd+Q 完全退出再重新打开</strong>；CLI 请新开一个终端。</p>
      `
    },
    {
      id: "user-windows",
      title: "Windows / WSL",
      summary: "先决定你是在 PowerShell 里跑，还是在 WSL 里跑，两个环境不要混配。",
      body: `
        <h4>PowerShell</h4>
        <pre><code>powershell -ExecutionPolicy Bypass -File ./configure-codex-seektoken.ps1</code></pre>
        <p>脚本默认使用 <code>${escapeHtml(SEEKTOKEN_GATEWAY_BASE_URL)}</code>，会隐藏 Key 输入，并写入 <code>$HOME&#92;.codex&#92;config.toml</code> 与 <code>$HOME&#92;.codex&#92;auth.json</code>。</p>
        <h4>WSL</h4>
        <p>如果你在 WSL 内运行 Codex，请在 WSL 终端中运行上面的 POSIX 脚本。</p>
        <div class="docs-callout">
          <strong>判断方法</strong>
          <p>只要你在 WSL 里执行 <code>codex</code>，两份文件就必须位于 WSL 的 <code>~/.codex/</code>。</p>
        </div>
      `
    },
    {
      id: "user-server",
      title: "服务器 / CI",
      summary: "服务器与 CI 仍然写两份文件；API Key 从挂载式 secret 文件读取，不经过环境变量。",
      body: `
        <h4>无交互执行</h4>
        <pre><code>./configure-codex-seektoken.sh \
  --api-key-file /run/secrets/seektoken-api-key \
  --model gpt-5.4</code></pre>
        <h4>Secret Manager / CI</h4>
        <ul>
          <li>把 Key 以只读 secret 文件挂载到任务中。</li>
          <li>用 <code>--api-key-file</code> 读取该文件，脚本不会在日志中输出 Key。</li>
          <li>任务完成后按平台规则卸载或销毁临时 secret。</li>
        </ul>
        <p>脚本会备份旧文件，并将新配置权限设为仅当前用户可读写。</p>
      `
    },
    {
      id: "user-console",
      title: "用户控制台怎么用",
      summary: "用户侧日常自助动作都在控制台里完成。",
      body: `
        <table class="docs-table">
          <thead>
            <tr>
              <th>标签页</th>
              <th>你可以做什么</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>概览</code></td>
              <td>查看 API Key、Token、充值、计费与积分汇总，以及快速入口</td>
            </tr>
            <tr>
              <td><code>API Key</code></td>
              <td>创建、复制、启停、删除自己的 Key</td>
            </tr>
            <tr>
              <td><code>钱包履历</code></td>
              <td>查看余额、钱包流水并导出记录</td>
            </tr>
            <tr>
              <td><code>订单</code></td>
              <td>提交充值订单并跟踪订单状态</td>
            </tr>
            <tr>
              <td><code>使用记录</code></td>
              <td>查看请求历史、错误、模型消耗</td>
            </tr>
            <tr>
              <td><code>设置</code></td>
              <td>维护账户资料和登录密码</td>
            </tr>
            <tr>
              <td><code>使用文档</code></td>
              <td>随时回看接入说明并下载 Markdown</td>
            </tr>
          </tbody>
        </table>
        <div class="docs-callout">
          <strong>建议</strong>
          <p>遇到问题先看这里的文档和自己的使用记录，再联系管理员。这样管理员更容易快速判断是配置问题还是服务问题。</p>
        </div>
      `
    },
    {
      id: "user-faq",
      title: "常见问题",
      summary: "先按报错类型判断，不要盲目重复安装或反复换 Key。",
      body: `
        <table class="docs-table">
          <thead>
            <tr>
              <th>现象</th>
              <th>通常原因</th>
              <th>处理方式</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>401 Invalid API key</td>
              <td>Key 无效、<code>auth.json</code> 格式错误或复制不完整</td>
              <td>检查 <code>auth.json</code> 的 <code>OPENAI_API_KEY</code>，必要时重新生成 Key</td>
            </tr>
            <tr>
              <td>桌面 App 提示 token 无法刷新</td>
              <td>桌面 App 尚未重新读取两份配置文件</td>
              <td>确认文件位于 <code>~/.codex/</code>，然后 Cmd+Q 完全退出 App 再打开</td>
            </tr>
            <tr>
              <td>429</td>
              <td>触发配额或频率限制</td>
              <td>先看自己的用量页，必要时联系管理员扩容或充值</td>
            </tr>
            <tr>
              <td>502 / 503</td>
              <td>上游服务异常，不一定是你的配置问题</td>
              <td>记录时间和模型名，联系管理员排查后台健康状态</td>
            </tr>
            <tr>
              <td>改完配置不生效</td>
              <td>文件路径错误，或当前 App 尚未重启</td>
              <td>核对当前用户的 <code>.codex</code> 目录；桌面 App 完全退出后重开</td>
            </tr>
          </tbody>
        </table>
      `
    }
  ]
};

export function renderAdminGuide(): string {
  return renderGuide(adminGuide);
}

export function renderUserGuide(): string {
  return renderGuide(userGuide);
}
